import { useCallback, useEffect, useRef, useState } from 'react';

function useScrollTo(id, options, optionalElem, watchInterval = 200) {
  const [hasInitScrolled, setHasInitScrolled] = useState(false);
  const intervalScrollRef = useRef();
  const totalWatchTimeRef = useRef(0);
  const MIN_WATCH_TIME = 600;

  let config = {
    behavior: 'smooth',
    initialScrollDelay: 750,
    offset: 0,
    shouldScrollIntoView: false,
    shouldWatchPosition: false
  };

  if (options && typeof options === 'object') {
    config = {
      ...config,
      ...options
    };
  }
  const configRef = useRef(config);
  const hash = window.location.hash;
  const path = window.location.pathname;
  const scroll = useCallback(
    scrollConfig => {
      try {
        const config = {
          ...configRef.current,
          ...(scrollConfig || {})
        };
        const { behavior, block, offset, shouldScrollIntoView } = config;
        const elem = config.elem || document.querySelector(id) || optionalElem;
        const top = elem
          ? elem.getBoundingClientRect().top + (window.scrollY - offset)
          : null;

        if (shouldScrollIntoView && elem) {
          elem.scrollIntoView({ behavior, block });
        } else if (top) {
          window.scrollTo({
            behavior,
            left: 0,
            top
          });
        }

        if (
          (window.scrollY >= Math.floor(top) ||
            (shouldScrollIntoView && elem)) &&
          intervalScrollRef.current &&
          totalWatchTimeRef.current >= MIN_WATCH_TIME
        ) {
          clearInterval(intervalScrollRef.current);
        }

        setHasInitScrolled(true);
      } catch (err) {
        if (process.env.__SITE_ENV__ === 'local') {
          console.error(err);
        }
      }
    },
    [id, optionalElem]
  );

  useEffect(() => {
    const config = configRef.current;
    if (id && hash === id) {
      setTimeout(() => {
        scroll();
      }, config.initialScrollDelay);
    }
  }, [
    hash,
    id,
    path /* handle scenarios like store detail to store detail navigation where the hash stays the same but the path changes. */,
    scroll
  ]);

  useEffect(() => {
    if (hasInitScrolled && configRef.current.shouldWatchPosition) {
      intervalScrollRef.current = setInterval(() => {
        scroll();
        totalWatchTimeRef.current += watchInterval;
      }, watchInterval);
    }

    return () => {
      if (intervalScrollRef.current) clearInterval(intervalScrollRef.current);
    };
  }, [hasInitScrolled, scroll, watchInterval]);

  const delayedScroll = useCallback(() => {
    setTimeout(scroll, 50); // allow other scrolls to be triggered first
  }, [scroll]);

  return delayedScroll;
}

export default useScrollTo;
